import { useAuthContext } from "../../../auth/state/AuthContext";
import { useAdminFlowRuns } from "../../hooks/useAdminFlowRuns";
import type {
  AdminFlowDefinition,
  AdminFlowDraftMessage,
  AdminFlowSavedDraft,
  AdminFlowsWorkspaceProps,
} from "../../models/admin-flows.models";
import {
  deleteSavedAdminFlowDraft,
  listSavedAdminFlowDrafts,
  saveAdminFlowDraft,
} from "../../services/admin-flows.service";
import { getAdminAgentErrorMessage } from "../../utils/admin-agent.utils";
import { createAdminFlow, getAdminFlowToolNames, validateAdminFlow } from "../../utils/admin-flows.utils";
import AdminFlowAutoBuilder from "./AdminFlowAutoBuilder";
import AdminFlowCards from "./AdminFlowCards";
import AdminFlowHistory from "./AdminFlowHistory";
import AdminFlowReview from "./AdminFlowReview";
import AdminFlowSchedule from "./AdminFlowSchedule";
import AdminFlowToolbar from "./AdminFlowToolbar";
import AdminFlowWhiteboard from "./AdminFlowWhiteboard";
import { useCallback, useEffect, useRef, useState } from "react";

// Own drafts and editor selection at the feature page, never in the admin route outlet.
// Each editor receives the same graph; edits save automatically only when the backend enables development SQL.
const AdminFlowsWorkspace = ({ view, onSelectView: setView }: AdminFlowsWorkspaceProps) => {
  const token = useAuthContext().state.user?.accessTokenRaw;
  const [flows, setFlows] = useState<AdminFlowDefinition[]>(() => [createAdminFlow()]);
  const [selectedId, setSelectedId] = useState<string>();
  const [selectedRunId, setSelectedRunId] = useState<string>();
  const [notice, setNotice] = useState("");
  const [errors, setErrors] = useState<string[]>([]);

  // Sidebar navigation changes the presentation while the workspace retains its drafts and run selection.
  useEffect(() => setErrors([]), [view]);
  const [messages, setMessages] = useState<Record<string, AdminFlowDraftMessage[]>>({});
  const [sqlEnabled, setSqlEnabled] = useState(false);
  const [storageReady, setStorageReady] = useState(false);
  const [storageError, setStorageError] = useState("");
  const [savedIds, setSavedIds] = useState<string[]>([]);
  const [savedDrafts, setSavedDrafts] = useState<Record<string, string>>({});
  const [editedIds, setEditedIds] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [storageRefresh, setStorageRefresh] = useState(0);
  const saveQueue = useRef(Promise.resolve());
  const deletingIds = useRef(new Set<string>());
  const lifetime = useRef(new AbortController());
  const currentDrafts = useRef<Record<string, string>>({});
  currentDrafts.current = Object.fromEntries(
    flows.map((candidate) => [
      candidate.id,
      JSON.stringify({ flow: candidate, messages: messages[candidate.id] ?? [] }),
    ]),
  );
  const savedDraftsRef = useRef(savedDrafts);
  savedDraftsRef.current = savedDrafts;
  const editedSinceLoad = useRef(false);
  const runtime = useAdminFlowRuns(token);
  const flow = flows.find((entry) => entry.id === selectedId) ?? flows[0];
  const tools = runtime.tools.filter((tool) => tool.executionPolicy !== "native_email_action");
  const flowSnapshot = JSON.stringify({ flow, messages: messages[flow.id] ?? [] });
  const dirty = editedIds.includes(flow.id) && savedDrafts[flow.id] !== flowSnapshot;
  const saveStatus = storageError
    ? "Save unavailable"
    : !storageReady
      ? "Loading storage…"
      : !sqlEnabled
        ? "Temporary draft"
        : saving
          ? "Saving…"
          : dirty
            ? "Unsaved changes"
            : savedIds.includes(flow.id)
              ? "Saved"
              : "New draft";

  useEffect(() => {
    const controller = new AbortController();
    lifetime.current = controller;

    return () => controller.abort();
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    listSavedAdminFlowDrafts(token, controller.signal)
      .then(({ sqlEnabled: enabled, drafts }) => {
        if (controller.signal.aborted) return;
        setSqlEnabled(enabled);
        setStorageReady(true);
        setStorageError("");
        setSavedIds(drafts.map((draft) => draft.flow.id));
        setSavedDrafts(Object.fromEntries(drafts.map((draft) => [draft.flow.id, JSON.stringify(draft)])));

        if (enabled && drafts.length && !editedSinceLoad.current) {
          setFlows(drafts.map((draft) => draft.flow));
          setMessages(Object.fromEntries(drafts.map((draft) => [draft.flow.id, draft.messages])));
          setSelectedId(drafts[0].flow.id);
        }
      })
      .catch((reason) => {
        if (!controller.signal.aborted) {
          setStorageReady(false);
          setStorageError(getAdminAgentErrorMessage(reason));
        }
      });

    return () => controller.abort();
  }, [token, storageRefresh]);

  const changeFlow = (next: AdminFlowDefinition) => {
    editedSinceLoad.current = true;
    setEditedIds((current) => (current.includes(flow.id) ? current : [...current, flow.id]));
    setFlows((current) =>
      current.map((entry) =>
        entry.id === flow.id ? { ...next, id: flow.id, revision: entry.revision + 1, status: "draft" } : entry,
      ),
    );
    setNotice("");
    setErrors([]);
  };

  const createFlow = () => {
    editedSinceLoad.current = true;
    const next = createAdminFlow();
    setEditedIds((current) => [...current, next.id]);
    setFlows((current) => [...current, next]);
    setSelectedId(next.id);
    setView("cards");
    setNotice("");
    setErrors([]);
  };

  const checkFlow = (candidate: AdminFlowDefinition) => {
    const issues = validateAdminFlow(candidate);
    candidate.nodes
      .filter((node) => node.kind === "agent")
      .forEach((node) => {
        if (getAdminFlowToolNames(node).some((name) => !tools.some((tool) => tool.name === name)))
          issues.push(`${node.label}: the selected tool is unavailable for your current permissions.`);
      });
    setErrors(issues);

    return issues.length === 0;
  };

  const saveFlow = useCallback(
    async (candidate: AdminFlowDefinition, automatic = false) => {
      if (!storageReady) {
        setStorageError("AI flow storage has not loaded. Refresh the workspace before saving.");

        return false;
      }

      if (!sqlEnabled) {
        setNotice("Draft kept in this workspace's state. Leaving or refreshing clears it.");

        return true;
      }

      const draft = { flow: candidate, messages: messages[candidate.id] ?? [] };
      const snapshot = JSON.stringify(draft);
      const controller = lifetime.current;
      let saved = false;

      // Serialize writes so a slow response cannot let an older edit replace a newer revision.
      const operation = saveQueue.current.then(async () => {
        if (controller.signal.aborted || deletingIds.current.has(candidate.id)) return;
        if (automatic && savedDraftsRef.current[candidate.id] === snapshot) return;
        if (automatic && currentDrafts.current[candidate.id] !== snapshot) return;

        const previousSnapshot = savedDraftsRef.current[candidate.id];
        const previous = previousSnapshot ? (JSON.parse(previousSnapshot) as AdminFlowSavedDraft) : undefined;

        // A queued draft must never undo a completed publish of the same revision.
        if (
          automatic &&
          previous?.flow.revision === candidate.revision &&
          previous.flow.status === "published" &&
          candidate.status === "draft"
        )
          return;

        setSaving(true);
        setStorageError("");

        try {
          if (new TextEncoder().encode(snapshot).length > 90 * 1024) {
            throw new Error("Saved flows must stay below 90 KB, including Auto Builder messages.");
          }

          await saveAdminFlowDraft(token, draft, controller.signal);

          if (controller.signal.aborted) return;

          savedDraftsRef.current = { ...savedDraftsRef.current, [candidate.id]: snapshot };
          setSavedDrafts(savedDraftsRef.current);
          setSavedIds((current) => (current.includes(candidate.id) ? current : [...current, candidate.id]));
          if (!automatic) setNotice("Flow saved in development Snowflake. Background scheduling remains inactive.");
          saved = true;
        } catch (reason) {
          if (!controller.signal.aborted) setStorageError(getAdminAgentErrorMessage(reason));
        } finally {
          if (!controller.signal.aborted) setSaving(false);
        }
      });
      saveQueue.current = operation.catch(() => undefined);
      await operation;

      return saved;
    },
    [storageReady, sqlEnabled, messages, token],
  );

  // Saving is enabled only by the existing local/DEV capability returned by the backend.
  // A new empty workspace is not written until the administrator edits it.
  useEffect(() => {
    if (!storageReady || !sqlEnabled) return;

    const pending = flows.filter(
      (candidate) =>
        editedIds.includes(candidate.id) &&
        savedDrafts[candidate.id] !== JSON.stringify({ flow: candidate, messages: messages[candidate.id] ?? [] }),
    );

    if (!pending.length) return;

    const timer = setTimeout(() => {
      pending.forEach((candidate) => void saveFlow(candidate, true));
    }, 800);

    return () => clearTimeout(timer);
  }, [flows, messages, editedIds, savedDrafts, storageReady, sqlEnabled, saveFlow]);

  useEffect(() => {
    const hasUnsaved = flows.some(
      (candidate) =>
        editedIds.includes(candidate.id) &&
        savedDrafts[candidate.id] !== JSON.stringify({ flow: candidate, messages: messages[candidate.id] ?? [] }),
    );

    if (!hasUnsaved) return;

    const warnBeforeReload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warnBeforeReload);

    return () => window.removeEventListener("beforeunload", warnBeforeReload);
  }, [flows, messages, editedIds, savedDrafts]);

  const deleteFlow = async () => {
    if (!sqlEnabled || !savedIds.includes(flow.id)) return;

    const deletedId = flow.id;
    deletingIds.current.add(deletedId);

    try {
      // Let accepted saves finish before deletion so they cannot recreate the deleted graph.
      await saveQueue.current;
      setStorageError("");
      await deleteSavedAdminFlowDraft(token, flow.id);
      editedSinceLoad.current = true;
      setSavedIds((current) => current.filter((id) => id !== deletedId));
      setEditedIds((current) => current.filter((id) => id !== deletedId));
      setMessages((current) => {
        const next = { ...current };
        delete next[deletedId];

        return next;
      });
      setFlows((current) => {
        const remaining = current.filter((entry) => entry.id !== deletedId);

        return remaining.length ? remaining : [createAdminFlow()];
      });
      setSelectedId(undefined);
      setNotice("Saved flow deleted. Existing run history remains available.");
    } catch (reason) {
      deletingIds.current.delete(deletedId);
      setStorageError(getAdminAgentErrorMessage(reason));
    }
  };

  const publish = async () => {
    if (!checkFlow(flow)) return;

    const published = { ...flow, status: "published" as const };

    if (!(await saveFlow(published))) return;

    setFlows((current) =>
      current.map((entry) =>
        entry.id === published.id && entry.revision === published.revision ? { ...entry, status: "published" } : entry,
      ),
    );
    setNotice(
      sqlEnabled
        ? "Published flow saved in development Snowflake. This does not enable a schedule or send email."
        : "Published in this temporary workspace. This does not enable a schedule or send email.",
    );
  };

  const startRun = async (candidate: AdminFlowDefinition) => {
    if (!checkFlow(candidate)) return;

    const run = await runtime.start(candidate);

    if (run) {
      setSelectedRunId(run.id);
      setView("review");
      setNotice("");
    }
  };

  const editRunFlow = (snapshot: AdminFlowDefinition) => {
    editedSinceLoad.current = true;
    // Open a copy so reviewing an old revision never overwrites a newer working draft.
    const copy = {
      ...snapshot,
      id: crypto.randomUUID(),
      name: `${snapshot.name.slice(0, 108)} (run copy)`,
      status: "draft" as const,
      schedule: { ...snapshot.schedule, enabled: false },
    };
    setEditedIds((current) => [...current, copy.id]);
    setFlows((current) => [...current, copy]);
    setSelectedId(copy.id);
    setView("whiteboard");
    setErrors([]);
  };

  return (
    <main data-test="admin-flows-workspace" className="flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
      <AdminFlowToolbar
        flow={flow}
        flows={flows}
        toolCount={tools.length}
        busy={runtime.busy || runtime.runs.some((run) => run.status === "running")}
        persistent={sqlEnabled}
        canDelete={sqlEnabled && savedIds.includes(flow.id)}
        saveStatus={saveStatus}
        saving={saving}
        onSelectFlow={(id) => {
          setSelectedId(id);
          setNotice("");
          setErrors([]);
        }}
        onChangeName={(name) => changeFlow({ ...flow, name })}
        onCreate={createFlow}
        onSave={() => void saveFlow(flow)}
        onDelete={() => void deleteFlow()}
        onTest={() => void startRun(flow)}
        onPublish={() => void publish()}
      />
      {storageError && (
        <div role="alert" className="flex shrink-0 items-center gap-2 bg-red-50 px-4 py-1 text-xs text-red-700">
          <span>{storageError}</span>
          <button
            data-test="admin-flow-storage-retry"
            className="font-semibold underline"
            onClick={() => (storageReady ? void saveFlow(flow) : setStorageRefresh((current) => current + 1))}
          >
            Retry {storageReady ? "save" : "storage"}
          </button>
        </div>
      )}
      {notice && (
        <p role="status" className="shrink-0 px-4 py-1 text-xs text-green">
          {notice}
        </p>
      )}
      {runtime.error && (
        <div
          role="alert"
          className="flex shrink-0 flex-wrap items-center gap-2 bg-red-50 px-4 py-1.5 text-xs text-red-700"
        >
          <span>{runtime.error}</span>
          <button data-test="admin-flow-runtime-retry" className="font-semibold underline" onClick={runtime.reload}>
            Refresh connection
          </button>
        </div>
      )}
      {errors.length > 0 && (
        <div
          role="alert"
          data-test="admin-flow-validation"
          className="max-h-28 shrink-0 overflow-y-auto bg-red-50 px-4 py-2 text-xs text-red-700"
        >
          <p className="font-semibold">Complete these settings</p>
          <ul className="mt-2 list-disc space-y-1 pl-5">
            {errors.map((error) => (
              <li key={error}>{error}</li>
            ))}
          </ul>
        </div>
      )}
      <div
        data-test="admin-flow-view-content"
        className={`min-h-0 min-w-0 flex-1 p-3 ${view === "whiteboard" ? "overflow-hidden" : "overflow-y-auto"}`}
      >
        {view === "whiteboard" && (
          <AdminFlowWhiteboard key={flow.id} flow={flow} tools={tools} onChange={changeFlow} fillHeight />
        )}
        {view === "cards" && <AdminFlowCards key={flow.id} flow={flow} tools={tools} onChange={changeFlow} />}
        {view === "auto" && (
          <AdminFlowAutoBuilder
            key={flow.id}
            flow={flow}
            tools={tools}
            onChange={changeFlow}
            token={token}
            messages={messages[flow.id] ?? []}
            onMessagesChange={(next) => {
              editedSinceLoad.current = true;
              setEditedIds((current) => (current.includes(flow.id) ? current : [...current, flow.id]));
              setMessages((current) => ({ ...current, [flow.id]: next }));
            }}
          />
        )}
        {view === "schedule" && <AdminFlowSchedule key={flow.id} flow={flow} onChange={changeFlow} />}
        {view === "history" && (
          <AdminFlowHistory
            runs={runtime.runs}
            onReview={(id) => {
              setSelectedRunId(id);
              setView("review");
            }}
            onRefresh={runtime.reload}
          />
        )}
        {view === "review" && (
          <AdminFlowReview
            key={selectedRunId ?? "empty"}
            id={selectedRunId}
            token={token}
            onEdit={editRunFlow}
            onRunAgain={(snapshot) => void startRun(snapshot)}
            onStop={(id) => void runtime.stop(id)}
            busy={runtime.busy}
          />
        )}
      </div>
    </main>
  );
};

export default AdminFlowsWorkspace;
